// v4 crystal — REDESIGNED. A GEM GROWTH off a tilted rock pedestal — one
// tall main prism (a faceted column with a pyramid tip) flanked by satellite
// prisms leaning outward at angles, a glinting chip broken off in front and
// scree around the foot. Each variant is its own mineral: ice-blue, violet
// or emerald-teal, with a pale tip so the spires read as lit from within.
//
// Ported 1:1 from age-of-mcp's v4/crystal.ts. The v4 `scree` helper (with its
// `h01`/`lerp`) is inlined locally because v4's variant differs from the
// library's `mineral.scree`.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function scree(
  seed: number, count: number,
  dist: readonly [number, number], size: readonly [number, number],
  colors: readonly string[],
): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + h01(seed, i * 4) * 1.6;
    const d = lerp(dist[0], dist[1], h01(seed, i * 4 + 1));
    const r = lerp(size[0], size[1], h01(seed, i * 4 + 2));
    const shard = i % 2 === 1;
    parts.push({
      geometry: shard
        ? new THREE.TetrahedronGeometry(r * 0.95, 0)
        : new THREE.IcosahedronGeometry(r, 0),
      color: colors[i % colors.length]!,
      position: [Math.cos(ang) * d, shard ? r * 0.28 : r * 0.5, Math.sin(ang) * d],
      rotation: [
        h01(seed, i * 4 + 3) * 0.9,
        h01(seed, i * 7 + 11) * Math.PI * 2,
        h01(seed, i * 5 + 23) * 0.9,
      ],
      scale: [1.05, 0.92, 1.0],
    });
  }
  return parts;
}

const ROCK  = "#55585f";
const ROCK2 = "#62656c";

// Per-variant mineral palette: [column, shaded column, pale tip].
const GEMS: ReadonlyArray<readonly [string, string, string]> = [
  ["#6fa3ef", "#5681cc", "#cfe4ff"],   // ice blue
  ["#a97fe3", "#8a5fc4", "#e6d4ff"],   // violet
  ["#4fc2a4", "#3a9d85", "#c9f3e6"],   // emerald teal
];

// One prism: a 6-sided column with a matching pyramid tip, leaning by
// (tiltX, tiltZ) and pivoting at its base so the foot stays planted.
function prism(
  x: number, z: number, r: number, h: number,
  tiltX: number, tiltZ: number, col: string, tip: string,
): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(r * 0.78, r, h, 6), color: col,
      position: [x, h / 2, z], rotation: [tiltX, 0, tiltZ] },
    { geometry: new THREE.ConeGeometry(r * 0.78, r * 1.7, 6), color: tip,
      // seat the tip on the leaned column's top end
      position: [
        x + Math.sin(tiltZ) * -h, (h + r * 0.8) * Math.cos(tiltX) * Math.cos(tiltZ),
        z + Math.sin(tiltX) * h,
      ],
      rotation: [tiltX, 0, tiltZ] },
  ];
}

function partsFor(v: number): Part[] {
  const seed = v * 17 + 9;
  const [gem, gemDark, tip] = GEMS[v % GEMS.length]!;
  const yaw = v * 1.4;
  const parts: Part[] = [
    // tilted rock pedestal the growth erupts from
    { geometry: new THREE.IcosahedronGeometry(0.17, 0), color: ROCK,
      position: [0, 0.09, 0], rotation: [0.15, yaw, -0.1],
      scale: [1.35, 0.62, 1.15] },
    { geometry: new THREE.TetrahedronGeometry(0.1, 0), color: ROCK2,
      position: [-0.11, 0.08, 0.06], rotation: [0.4, yaw + 1.2, 0.2],
      scale: [1.0, 0.9, 1.0] },
  ];

  // Main spire plus three satellites leaning outward.
  parts.push(...prism(0, 0, 0.075, lerp(0.34, 0.4, h01(seed, 1)), 0.04, -0.06, gem, tip));
  parts.push(...prism(0.13, 0.05, 0.05, 0.2, 0.05, -0.3, gemDark, tip));
  parts.push(...prism(-0.11, 0.07, 0.045, 0.17, 0.18, 0.26, gemDark, tip));
  parts.push(...prism(0.04, -0.13, 0.04, 0.14, -0.3, 0.08, gem, tip));

  // A glinting chip broken off against the pedestal — kept tucked to the
  // base and lifted so no vertex dips below the ground plane.
  parts.push({
    geometry: new THREE.TetrahedronGeometry(0.04, 0), color: tip,
    position: [0.11, 0.06, -0.09],
    rotation: [h01(seed, 5) * 1.1, yaw + 0.6, 0.4],
    scale: [1.0, 1.05, 0.9],
  });

  parts.push(...scree(seed, 3, [0.1, 0.15], [0.028, 0.045], [ROCK, ROCK2]));
  return parts;
}

export const crystalV4 = defineModel({
  id: "crystal-v4",
  batchId: "polished",
  name: "Crystal (v4)",
  tags: ["scenery","crystal","low-poly","age-of-mcp","fable"],
  summary: "Low-poly crystal.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.36,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
    { id: "variant-2", label: "Variant 3", parts: () => partsFor(2) },
  ],
  animations: [],
});
